import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { getSchool, getStudentGroup } from '@/lib/api-calls.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { formatStudentGrades } from '@/lib/student-group-utils.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list.js';
import { LevelChangeWarning } from '@/routes/_layout/orgs/schools/-components/level-change-warning.js';
import { StudentGroupStatusBadge } from '@/routes/_layout/scheduling/student-groups/-components/student-group-status-badge.js';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { adjustMinimalClassTimeAcrossTZ } from '@airreading/common/data';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit } from 'lucide-react';
import { getCoachOverallGradeLabel } from '@/lib/coach-utils.js';
import { CreateEditClass } from '../../-components/create-edit-class.js';
import { ReassignmentRecordsCard } from '../../-components/reassignment-records-card.js';
import { schoolDetailsTabs } from '../../-components/tabs.js';
import { AttendanceStatusBadge } from '@/routes/_layout/teachers/-components/attendance-status-badge.tsx.js';
import { EmailsSentBadge } from '@/routes/_layout/teachers/-components/emails-sent-badge.js';
import { Textarea } from '@/components/ui/textarea.js';
import { pluralize } from '@/lib/string-utils.js';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/classes/$classId')({
  component: SchoolClassesDetailPage,
});

function SchoolClassesDetailPage() {
  const { schoolId } = Route.useParams();

  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load class detail. Please try again.',
          back: {
            backLink: {
              to: '/orgs/schools/$schoolId/classes',
              params: { schoolId },
            },
          },
        };
      }}
    >
      <SchoolClassesDetail schoolId={schoolId} />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolClassesDetail({ schoolId }: { schoolId: string }) {
  const { classId } = Route.useParams();

  const { data } = useSuspenseQuery(getSchool(schoolId));
  const { data: classData } = useSuspenseQuery(getStudentGroup(classId, { includeLevelChangeWarning: true }));
  const {
    coach,
    students,
    grades,
    classTime,
    futureSessions,
    startingLesson,
    nextLesson,
    overrideStartPlainDate,
    overrideEndPlainDate,
    attendanceStatus,
    emailsSent,
    attendanceNotes,
    notes,
    maxPlannedAbsences,
  } = classData;
  const schoolTimeZone = data.org.timeZone;
  const { schedule } = classTime;

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
          {
            label: data.name,
            link: {
              to: '/orgs/schools/$schoolId',
              params: { schoolId },
            },
          },
        ],
        currentPage: 'Class Details',
      }}
      tabs={schoolDetailsTabs({ schoolId }, 'classes')}
    >
      <InfoCard
        headerText="Class Details"
        headerActions={
          <div className="flex gap-2">
            <CreateEditClass
              trigger={<Button icon={Edit}>Edit</Button>}
              schoolId={schoolId}
              from={classData}
              schoolTimeZone={schoolTimeZone}
            />
          </div>
        }
      >
        <InfoKeyValue
          label="Teacher"
          value={coach ? <Badge variant={'outline'}>{coach.fullNameOrEmail}</Badge> : <div>No teacher assigned</div>}
        />
        <InfoKeyValue
          label="Students"
          value={<PeopleBadgeList people={students} wrap={true} renderExtra={LevelChangeWarning} />}
        />
        <InfoKeyValue label="Grades" value={formatStudentGrades(grades)} />
        <InfoKeyValue label="Status" value={<StudentGroupStatusBadge studentGroupLike={classData} />} />
        <InfoKeyValue label="Schedule" value={<Badge variant={'outline'}>{schedule.name}</Badge>} />
        <InfoKeyValue
          label={<span>Class time (CT)</span>}
          value={
            <Badge variant={'outline'}>
              {formatStudentClassTimeAbbr(
                adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
              )}
            </Badge>
          }
        />
        <InfoKeyValue label="Start date" value={formatPlainDate(schedule.startPlainDate)} />
        <InfoKeyValue label="End date" value={formatPlainDate(schedule.endPlainDate)} />
        <InfoKeyValue
          label="Override start date"
          value={overrideStartPlainDate ? formatPlainDate(overrideStartPlainDate) : 'N/A'}
        />
        <InfoKeyValue
          label="Override end date"
          value={overrideEndPlainDate ? formatPlainDate(overrideEndPlainDate) : 'N/A'}
        />
        <InfoKeyValue label="Number of future sessions" value={futureSessions} />
        <InfoKeyValue label="Starting lesson" value={startingLesson.name} />
        <InfoKeyValue label="Next lesson" value={nextLesson ? nextLesson.name : ''} />
        <InfoKeyValue label="Overall grade" value={getCoachOverallGradeLabel(coach?.coachProfile.overallGrade)} />
        <InfoKeyValue
          label="Attendance status"
          value={attendanceStatus ? <AttendanceStatusBadge attendanceStatus={attendanceStatus} /> : '-'}
        />
        <InfoKeyValue
          label="Emails sent"
          value={
            emailsSent?.length ? (
              <div className="flex items-center gap-2">
                {emailsSent.map((email) => (
                  <EmailsSentBadge key={email} emailsSent={email} />
                ))}
              </div>
            ) : (
              '-'
            )
          }
        />
        <InfoKeyValue
          label="Teacher attendance notes"
          value={
            attendanceNotes?.trim() ? <Textarea value={attendanceNotes} className="w-2/3 resize-none" readOnly /> : '-'
          }
        />
        <InfoKeyValue
          label="Sub request limit"
          value={
            maxPlannedAbsences === 0
              ? '-'
              : `${maxPlannedAbsences} ${pluralize(maxPlannedAbsences, 'class', 'classes')}`
          }
        />
        <InfoKeyValue label="Notes" value={notes?.trim() ?? '-'} />
      </InfoCard>
      <ReassignmentRecordsCard classId={classId} />
    </InfoPage>
  );
}
